import { useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowRightLeft, AlertTriangle } from "lucide-react";

const MOTIVOS = [
  { v: "divorcio", l: "Divórcio" },
  { v: "viuvez", l: "Viuvez" },
  { v: "ausencia", l: "Ausência prolongada / abandono do lar" },
  { v: "outro", l: "Outro (especificar)" },
];

export default function TransferirTitularidadeCard({ insc, onDone }) {
  const [motivo, setMotivo] = useState("divorcio");
  const [obs, setObs] = useState("");
  const [tid, setTid] = useState(null);
  const [otp, setOtp] = useState("");
  const [otpMock, setOtpMock] = useState(""); // dev fallback
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const podeTransferir = insc?.conjuge_nome && insc?.conjuge_cpf;

  const solicitar = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/inscricoes/${insc.id}/transferir/solicitar`, {
        motivo, observacao: obs,
      });
      setTid(r.data.transferencia_id);
      setOtpMock(r.data.otp_mock || "");
      toast.success(r.data.email_enviado
        ? `OTP enviado para ${r.data.email_destino}`
        : "OTP gerado — verifique via canal alternativo");
      setOpenConfirm(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao solicitar");
    } finally { setLoading(false); }
  };

  const confirmar = async () => {
    setLoading(true);
    try {
      await api.post(`/transferencias/${tid}/confirmar`, { otp });
      toast.success("Titularidade transferida com sucesso");
      setOpenConfirm(false);
      setOtp(""); setTid(null); setOtpMock("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "OTP inválido ou expirado");
    } finally { setLoading(false); }
  };

  const cancelar = async () => {
    if (!tid) return setOpenConfirm(false);
    if (!window.confirm("Cancelar a transferência em andamento?")) return;
    try {
      await api.post(`/transferencias/${tid}/cancelar`);
      toast.success("Transferência cancelada");
    } catch (e) {
      logger.warn("Falha ao cancelar transferência (prosseguindo com reset local)", e);
    }
    setTid(null); setOtp(""); setOtpMock(""); setOpenConfirm(false);
  };

  return (
    <>
      <Card className="border-amber-200 bg-amber-50/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 text-amber-800">
            <ArrowRightLeft className="w-5 h-5" />Transferir Titularidade
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!podeTransferir ? (
            <p className="text-xs text-slate-600">
              Não há cônjuge cadastrado. A transferência só é possível quando existe cônjuge.
            </p>
          ) : (
            <>
              <p className="text-xs text-slate-700">
                Transferir titularidade para <strong>{insc.conjuge_nome}</strong>{" "}
                (CPF {insc.conjuge_cpf}). Um OTP será enviado ao e-mail do titular atual para autorização.
              </p>
              <div>
                <Label className="text-xs">Motivo</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm mt-1"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  data-testid="transfer-motivo"
                >
                  {MOTIVOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Observação (opcional)</Label>
                <Input
                  value={obs}
                  onChange={(e) => setObs(e.target.value)}
                  placeholder="Detalhes do caso, processo, etc."
                  data-testid="transfer-obs"
                />
              </div>
              <Button
                onClick={solicitar} disabled={loading}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="btn-solicitar-transfer"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                {loading ? "Solicitando..." : "Solicitar transferência"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={openConfirm} onOpenChange={(o) => !o && cancelar()}>
        <DialogContent data-testid="dialog-confirmar-transfer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-6 h-6" />Confirmar Transferência
            </DialogTitle>
            <DialogDescription>
              Informe o código OTP que foi enviado ao e-mail do titular atual ({insc?.email}).
              O código expira em 30 minutos.
            </DialogDescription>
          </DialogHeader>
          {otpMock && (
            <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2">
              <strong>Modo dev:</strong> OTP <code className="font-mono">{otpMock}</code>
            </div>
          )}
          <div>
            <Label className="text-xs">Código OTP (6 caracteres)</Label>
            <Input
              value={otp}
              onChange={(e) => setOtp(e.target.value.toUpperCase())}
              maxLength={6}
              className="font-mono text-lg tracking-widest text-center"
              data-testid="input-otp-transfer"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelar}>Cancelar</Button>
            <Button
              onClick={confirmar}
              disabled={loading || otp.length < 6}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="btn-confirmar-otp"
            >
              {loading ? "Confirmando..." : "Confirmar transferência"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
